import _ from "lodash";
import moment from "moment";
import { createCalendar } from "../framework/components/fullcalendar";

/*
LiveView hook for the shared dates editor calendar (subsession slots + instructor absences).
The server-side LiveComponent pushes events:
  - "dates_editor:events" { events }: replace the event set and refetch
  - "dates_editor:select" { start, end }: programmatic selection (YYYY-MM-DD)
The client pushes back to the LC (via phx-target):
  - "select_range" { start, end }
*/
/**
 * @typedef {Object} CalendarEvent
 * @property {string} start ISO date
 * @property {string} [end] ISO date
 * @property {string} [title]
 * @property {string} [color]
 * @typedef {{ start: Date, end: Date } | null} CalendarSelection
 */
const DatesEditorCalendarHook = {
  /** @type {CalendarEvent[]} */
  events: [],
  /** @type {import("@fullcalendar/core").Calendar | null} */
  calendar: null,
  /** @type {CalendarSelection} */
  lastSelection: null,

  async mounted() {
    this.events = JSON.parse(this.el.dataset.initialEvents || "[]");

    this.handleEvent("dates_editor:events", ({ events }) => {
      this.events = events;
      this.calendar?.refetchEvents();
    });

    this.handleEvent("dates_editor:select", ({ start, end }) => {
      if (!this.calendar) return;
      const s = moment(start).toDate();
      const e = moment(end).add(1, "day").toDate();
      this.lastSelection = { start: s, end: e };
      this.calendar.select(s, e);
    });

    this.handleEvent("dates_editor:unselect", () => {
      if (!this.calendar) return;
      this.lastSelection = null;
      this.calendar.unselect();
    });

    // Clicking a slot title in the global conflict warning re-renders the slots
    // column and pushes this event; LiveView patches the DOM before dispatching
    // pushed events, so the target card already exists here.
    this.handleEvent("dates_editor:scroll_to_slot", ({ key }) => {
      const el = document.getElementById(`slot-card-${key}`);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    });

    const initialDateAttr = this.el.dataset.initialDate;
    let initialDate = new Date();
    if (initialDateAttr === "last" && this.events.length > 0) {
      initialDate = this.events[this.events.length - 1].start;
    } else if (initialDateAttr === "first" && this.events.length > 0) {
      initialDate = this.events[0].start;
    } else if (initialDateAttr && initialDateAttr !== "today") {
      initialDate = initialDateAttr;
    }

    this.calendar = await createCalendar(this.el, {
      // Slots are built from timezone-less info stored on the backend. We work with calendar
      // dates (not times), so local timezone avoids conversion.
      timeZone: "local",
      initialView: "dayGridMonth",
      height: "auto",
      dayMaxEventRows: false,
      initialDate: initialDate,
      selectable: true,
      unselectAuto: false,
      // Long-press to select is hard to discover; any touch triggers a selection. Revisit if
      // we ever add draggable events/handles, since a scroll intent could become destructive.
      longPressDelay: 0,
      displayEventTime: false,
      events: (_info, callback) => callback(this.events),
      select: ({ start, end, jsEvent, allDay }) => {
        // No jsEvent means programmatic selection (we triggered it ourselves); skip the round-trip.
        if (!jsEvent || !allDay) return;
        this.lastSelection = { start, end };
        const startStr = moment(start).format("YYYY-MM-DD");
        // FullCalendar's `end` is the day AFTER the last selected, so shift back by one to get the
        // inclusive end date our backend expects. Use date strings to dodge timezone conversion.
        const endStr = moment(end).add(-1, "day").format("YYYY-MM-DD");
        this._syncPeriodInputs(startStr, endStr);
        this.pushEventTo(this.el, "select_range", { start: startStr, end: endStr });
      },
      eventClick: ({ event }) => {
        const dateStr = moment(event.start).format("YYYY-MM-DD");
        const end = moment(event.start).add(1, "day").toDate();
        this.lastSelection = { start: event.start, end };
        this._syncPeriodInputs(dateStr, dateStr);
        this.pushEventTo(this.el, "select_range", { start: dateStr, end: dateStr });
        this.calendar.select(event.start);
      },
      // FullCalendar unselects on view change even with unselectAuto:false. We detect a view switch
      // by the absence of `jsEvent` (with `jsEvent`, it's a mouse click = a new user selection that
      // should NOT be restored). On view-switch, re-select the prior range so it doesn't appear lost.
      unselect: (info) => {
        if (!info.jsEvent && this.lastSelection) {
          this.calendar.select(this.lastSelection.start, this.lastSelection.end);
        }
      },
    });

    // Refetching is expensive with many events; throttle so back-to-back updates (e.g. a user
    // spamming the delete-slot button) collapse into one refresh.
    const oldRefetch = this.calendar.refetchEvents.bind(this.calendar);
    this.calendar.refetchEvents = _.throttle(oldRefetch, 250);

    const initialSelection = this.el.dataset.initialSelection;
    if (initialSelection) {
      const { start, end } = JSON.parse(initialSelection);
      const s = moment(start).toDate();
      const e = moment(end).add(1, "day").toDate();
      this.lastSelection = { start: s, end: e };
      this.calendar.select(s, e);
    }
  },
  destroyed() {
    this.calendar?.destroy();
  },
  // LiveView preserves the value of a focused input across diffs, so a server-driven
  // form re-render won't update a focused date input. Mirror the new range into the
  // DOM directly so the inputs reflect the calendar click even when focused.
  _syncPeriodInputs(start, end) {
    const root = this.el.closest(".slots-selector");
    if (!root) return;
    const startInput = root.querySelector('input[name="period[start]"]');
    const endInput = root.querySelector('input[name="period[end]"]');
    if (startInput) startInput.value = start;
    if (endInput) endInput.value = end;
  },
};

export default DatesEditorCalendarHook;
